<template>
    <VTextField
        ref="phoneInput"
        type="tel"
        v-model="val.number"
        :variant="variant"
        :label="label"
        :density="density"
        :hint="hint"
        :prefix="phonePrefix"
        :suffix="suffix"
        :messages="messages"
        :disabled="disabled"
        :clearable="clearable"
        :readonly="readonly"
        :loading="loading"
        :errorMessages="errorMessages"
        :placeholder="placeholder"
        :field-error="fieldError"
        @input="filterInput"
        @blur="handleBlur"
        autocomplete="tel">
        <template #prepend-inner>
            <button
                id="country-selector"
                :name="t({ key: 'aria.phone_country_code' })"
                :aria-label="t({ key: 'aria.phone_country_code' })"
                class="prepend-inner-button"
                @pointerdown.stop.prevent
                @keydown.enter.stop.prevent="openModal"
                @click.stop.prevent="openModal">
                <Flag v-if="val.countryCode" :code="val.countryCode" class="flag" />

                <Icon
                    class="prepend-inner-button-icon"
                    type="Chevron"
                    :rotate="showModal ? 180 : 0" />
            </button>

            <VDivider vertical class="prepend-inner-divider" />
        </template>
    </VTextField>

    <Modal
        v-if="showModal"
        :title="t({ key: 'shared.country' })"
        scroll="box"
        size="m"
        :onClose="closeModal">
        <div>
            <Textbox
                v-model="filter"
                :placeholder="t({ key: 'shared.search' })"
                @keydown.esc="closeModal()"
                autofocus />

            <VList class="menu-list" ref="menuList">
                <VListItem
                    v-for="country in visibleCountries"
                    :key="country.countryCode"
                    @click="() => selectCountry(country.countryCode)"
                    class="menu-list-item">
                    <template #prepend>
                        <Flag
                            :v-if="country.countryCode"
                            :code="country.countryCode"
                            class="flagIcon" />
                    </template>
                    <VListItemTitle class="list-item-title">
                        {{ `${country.name} +${country.dialCode}` }}
                    </VListItemTitle>
                </VListItem>
            </VList>
        </div>
    </Modal>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType, ref, watch, watchEffect } from 'vue';
    import { useInfiniteScroll } from '@vueuse/core';
    import {
        VTextField,
        VList,
        VListItem,
        VListItemTitle,
        VDivider,
    } from 'vuetify/components';
    import countryList from './Phone.helpers';
    import Flag from '@ui/components/Flag/Flag.vue';
    import { PhonePayload } from './Phone.interfaces';
    import { CountryCode, parsePhoneNumber, PhoneNumber } from 'libphonenumber-js';
    import {
        Density,
        Variant,
    } from '@ui/components/FormV2/Fields/Textbox/Textbox.interfaces';
    import { isValidPhoneNumber } from '@shared/helpers/validation';
    import Icon from '@ui/components/Icon/Icon.vue';
    import Modal from '@ui/components/Modal/Modal.vue';
    import Textbox from '@ui/components/FormV2/Fields/Textbox/Textbox.vue';

    export default defineComponent({
        name: 'Phone',
        methods: { t },
        components: {
            VTextField,
            VList,
            VListItem,
            VListItemTitle,
            VDivider,
            Icon,
            Modal,
            Flag,
            Textbox,
        },
        props: {
            modelValue: { type: String },
            countryCode: { type: String, default: 'SE' },
            label: String,
            placeholder: String,
            hint: String,
            suffix: String,
            loading: { type: Boolean as PropType<boolean>, default: false },
            disabled: { type: Boolean as PropType<boolean>, default: false },
            clearable: { type: Boolean as PropType<boolean>, default: false },
            readonly: { type: Boolean as PropType<boolean>, default: false },
            messages: { type: Array as PropType<string[]> },
            errorMessages: {
                type: [Array, String] as PropType<string[] | string>,
            },
            variant: { type: String as PropType<Variant>, default: 'outlined' },
            density: { type: String as PropType<Density>, default: 'default' },
            onBlur: Function,
            fieldError: Boolean,
        },
        emits: ['update:modelValue', 'update:countryCode'],
        setup(props, { emit }) {
            const phoneInput = ref<HTMLElement | undefined>(undefined);
            const count = 10;

            const menuList = ref(null); // Use useInfiniteScroll for lazy loading
            const showModal = ref(false);
            const filter = ref<string>('');

            const allCountries = computed(() => countryList);
            const unfilteredCountries = ref(allCountries.value);
            const visibleCountries = ref(allCountries.value.slice(0, count)); // Initial visible countries

            const val = ref<PhonePayload>({
                countryCode: props.countryCode as CountryCode,
                number: '',
            });

            const persistent = computed(() => Boolean(val.value.number));

            const phonePrefix = computed((): string => {
                const country = allCountries.value.find(
                    (c) => c.countryCode === val.value.countryCode
                );
                return country ? `+${country.dialCode}` : '';
            });

            const openModal = () => {
                showModal.value = true;
                filter.value = '';
            };

            const closeModal = () => {
                showModal.value = false;
                filter.value = '';
                phoneInput.value?.focus();
            };

            const selectCountry = (countryCode: CountryCode) => {
                val.value.countryCode = countryCode;
                closeModal();
                if (val.value.number) {
                    handleBlur();
                }
            };

            const filterInput = (event: any) => {
                val.value.number = event.target.value.replace(/[^0-9]/g, '');
            };

            function formatModelValue(value: string): PhonePayload {
                try {
                    const parsedNumber = parsePhoneNumber(value, val.value.countryCode);
                    return {
                        number: parsedNumber.nationalNumber,
                        countryCode: parsedNumber.country ?? val.value.countryCode,
                    };
                } catch {
                    return {
                        countryCode: val.value.countryCode,
                        number: value,
                    };
                }
            }

            function formatVal(value: PhonePayload): PhoneNumber | undefined {
                try {
                    return parsePhoneNumber(value.number, value.countryCode);
                } catch {
                    return undefined;
                }
            }

            const handleBlur = () => {
                onUpdateValue(val.value);
                props.onBlur && props.onBlur();
            };

            const onUpdateValue = (phone: PhonePayload) => {
                if (isValidPhoneNumber(phone.number, phone.countryCode)) {
                    const formattedPhone = formatVal(phone);
                    emit('update:modelValue', formattedPhone?.number);
                    emit('update:countryCode', formattedPhone?.country);
                } else {
                    emit('update:modelValue', undefined);
                    emit('update:countryCode', undefined);
                }
            };

            watch(
                () => props.modelValue,
                (newValue, prevValue) => {
                    const isUpdated = JSON.stringify(newValue) !== JSON.stringify(prevValue);

                    if (newValue && isUpdated) {
                        val.value = formatModelValue(newValue);
                        handleBlur();
                    }
                },
                { immediate: true }
            );

            watch(
                () => val.value,
                (newValue) => {
                    onUpdateValue(newValue);
                },
                { deep: true }
            );

            watchEffect(() => {
                const inputValue = filter.value.trim().toLowerCase();

                if (!inputValue) {
                    visibleCountries.value = unfilteredCountries.value.slice(0, count);
                } else {
                    visibleCountries.value = unfilteredCountries.value
                        .filter((country) => {
                            const dialCode = `+${country.dialCode}`;
                            return (
                                country.name.toLowerCase().includes(inputValue) ||
                                dialCode.includes(inputValue)
                            );
                        })
                        .slice(0, count);
                }
            });

            // Handle infinite scroll
            useInfiniteScroll(menuList, () => {
                const currentLength = visibleCountries.value.length;

                if (currentLength < unfilteredCountries.value.length) {
                    const additionalCountries = unfilteredCountries.value.slice(
                        currentLength,
                        currentLength + count
                    );
                    visibleCountries.value = [
                        ...visibleCountries.value,
                        ...additionalCountries,
                    ];
                }
            });

            return {
                val,
                filter,
                showModal,
                openModal,
                closeModal,
                filterInput,
                visibleCountries,
                phonePrefix,
                persistent,
                selectCountry,
                handleBlur,
                menuList,
                phoneInput,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Phone.scss';
</style>
